'use client';
import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAccountSwitcher } from '../hooks/useAccountSwitcher';
import { multiAccountAutoAdd, MultiAccountAutoAddError } from './multiAccountAutoAdd';

export interface HandoffLandingProps {
  /** Override the splash text. */
  splash?: string;
  /** Where to send the user if the URL is missing required params. Default `/login`. */
  loginPath?: string;
  /**
   * How long to wait for `<AccountSwitcherProvider>` to initialize its
   * PostMessage client AND complete the iframe handshake before giving up with
   * `handoff_provider_missing`.
   *
   * The provider creates the client in a mount effect (which runs *after* this
   * child mounts) and the iframe handshake is asynchronous, so HandoffLanding
   * must poll for readiness rather than bail on first render. Default 8000ms.
   */
  providerReadyTimeoutMs?: number;
}

/**
 * The page consumers drop at `app/auth/handoff/landing/page.tsx`:
 *
 * ```tsx
 * 'use client';
 * import { HandoffLanding } from '@benbraide/auth-service-nextjs/sharing';
 * export default function Page() { return <HandoffLanding />; }
 * ```
 *
 * Reads `account_uuid`, `session_token`, `next`, `toast` from the URL (set by
 * the F2 route handler), waits for the provider's PostMessage client to be
 * ready (iframe registered + handshake complete), runs `multiAccountAutoAdd`,
 * then redirects to `${next}?_toast=${toast}` so the arriving page's
 * `HandoffArrivalToast` can show the banner.
 *
 * Requirements on the consumer:
 * - This page must be rendered inside `<AccountSwitcherProvider>`.
 * - An `<AccountSwitcher>` iframe must be mounted somewhere in that tree so the
 *   provider's client has a registered iframe + completed handshake to post to.
 */
export function HandoffLanding({
  splash = 'Setting up your account…',
  loginPath = '/login',
  providerReadyTimeoutMs = 8000,
}: HandoffLandingProps) {
  const router = useRouter();
  const params = useSearchParams();
  const switcher = useAccountSwitcher();
  const fired = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest switcher in a ref so the poll loop always reads the live
  // client without re-arming the loop on every provider re-render.
  const switcherRef = useRef(switcher);
  switcherRef.current = switcher;

  useEffect(() => {
    if (fired.current) return;

    const accountUuid = params?.get('account_uuid');
    const sessionToken = params?.get('session_token');
    const next = params?.get('next') ?? '/';
    const toast = params?.get('toast') ?? '';

    if (!accountUuid || !sessionToken) {
      fired.current = true;
      router.replace(`${loginPath}?error=handoff_missing_params`);
      return;
    }

    if (startedAtRef.current === null) startedAtRef.current = Date.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const runAutoAdd = () => {
      fired.current = true;
      const current = switcherRef.current;
      void (async () => {
        try {
          await multiAccountAutoAdd({
            accountUuid,
            sessionToken,
            switcher: {
              addAccount: current.addAccount,
              switchAccount: current.switchAccount,
            },
            client: current.client!,
            sendMessage: current.sendMessage,
          });
          const dest = toast
            ? `${next}${next.includes('?') ? '&' : '?'}_toast=${encodeURIComponent(toast)}`
            : next;
          router.replace(dest);
        } catch (err) {
          const reason = err instanceof MultiAccountAutoAddError ? err.reason : 'unknown';
          setError(reason);
          router.replace(`${loginPath}?error=handoff_${reason}`);
        }
      })();
    };

    // Poll for the provider's client + completed iframe handshake. Fires the
    // auto-add the moment it's ready, or bails after the bounded window so the
    // user never sits on the splash forever.
    const tick = () => {
      if (cancelled || fired.current) return;

      const current = switcherRef.current;
      const ready = !!current.client && current.client.isReady() && !!current.sendMessage;
      if (ready) {
        runAutoAdd();
        return;
      }

      const elapsed = Date.now() - (startedAtRef.current ?? Date.now());
      if (elapsed >= providerReadyTimeoutMs) {
        fired.current = true;
        setError('provider_missing');
        router.replace(`${loginPath}?error=handoff_provider_missing`);
        return;
      }

      timer = setTimeout(tick, 150);
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // `switcher` intentionally omitted — the loop reads it via switcherRef so it
    // doesn't re-arm on every provider re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, router, loginPath, providerReadyTimeoutMs]);

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif',
        color: '#444',
      }}
      role="status"
      aria-live="polite"
    >
      {error ? `Sign-in failed (${error})…` : splash}
    </div>
  );
}
